import {
  CfDataGridColumnDefinition,
  CfDataGridForStoreDataProps,
  CfDataGridProps,
  CfDataGridRowType,
  CfDataGridTableViewDefinition,
  CfDataGridValueGetterProps,
  CfDraggableRowData,
  CfRowData,
  NavigableElement,
} from '../../../CfDataGridProps';
import { ValueGetter } from '../CfDataGridTableViewProps';
import { CfDataGridRowByItemIdProps, CfDataGridRowProps } from '../row/common';

import { ReactNode } from 'react';
import { CellMeasurerCache, List } from 'react-virtualized';
import { debounce } from 'throttle-debounce';
import { getFilterText, getHasItems, getItemIds, getItems } from '../../../utility/data-grid-props';
import { CfDataGridNoResultsFound } from '../../DataGridNoResultsFound/CfDataGridNoResultsFound';
import { RowWrappers, RowWrappersById } from '../row/CfDataGridVirtualizedRowWrappers';
import { GetDraggableRowData } from '../row/drag-drop-helpers';

export function createValueGetters<TItem>(columns: CfDataGridColumnDefinition<TItem>[]): ValueGetter<TItem>[] {
  return columns.map((col: CfDataGridColumnDefinition<TItem>) => ({
    column: col,
    getValue:
      typeof col.value === 'function'
        ? col.value
        : (props: CfDataGridValueGetterProps<TItem>) => props.item[col.value as keyof TItem] as ReactNode,
  }));
}

export function getRowDataById<TItem, TAppState>(props: CfDataGridRowByItemIdProps<TItem, TAppState>): CfRowData[] {
  const itemId = props.itemId;
  if (!itemId) {
    return [
      {
        id: itemId,
        type: 'row-skeleton',
        content: <RowWrappersById rowProps={props} rowType='row-skeleton' />,
      },
    ];
  }

  const rowData = [] as CfRowData[];

  const customRowBefore = <RowWrappersById rowProps={props} rowType='custom-row-before' />;
  if (customRowBefore && props.tableViewDefinition.insertCustomRow) {
    rowData.push({
      id: itemId,
      type: 'custom-row-before',
      content: customRowBefore,
    });
  }

  const row = <RowWrappersById rowProps={props} rowType='row' />;
  if (row) {
    rowData.push({
      id: itemId,
      type: 'row',
      content: row,
    });
  }

  const rowFooter = <RowWrappersById rowProps={props} rowType='row-footer' />;
  if (rowFooter && props.tableViewDefinition.insertFooterRow) {
    rowData.push({
      id: itemId,
      type: 'row-footer',
      content: rowFooter,
    });
  }

  const customRowAfter = <RowWrappersById rowProps={props} rowType='custom-row-after' />;
  if (customRowAfter && props.tableViewDefinition.insertCustomRow) {
    rowData.push({
      id: itemId,
      type: 'custom-row-after',
      content: customRowAfter,
    });
  }

  return rowData;
}

export function getRowData<TItem>(props: CfDataGridRowProps<TItem>, id?: string): CfRowData[] {
  const item = props.item;
  if (!item) {
    return [
      {
        id: id,
        type: 'row-skeleton',
        content: <RowWrappers rowProps={props} rowType='row-skeleton' />,
      },
    ];
  }

  const rowData = [] as CfRowData[];

  const customRowBefore = <RowWrappers rowProps={props} rowType='custom-row-before' />;
  if (customRowBefore && props.tableViewDefinition.insertCustomRow) {
    rowData.push({
      id: id,
      type: 'custom-row-before',
      content: customRowBefore,
    });
  }

  const row = <RowWrappers rowProps={props} rowType='row' />;
  if (row) {
    rowData.push({
      id: id,
      type: 'row',
      content: row,
    });
  }

  const rowFooter = <RowWrappers rowProps={props} rowType='row-footer' />;
  if (rowFooter && props.tableViewDefinition.insertFooterRow) {
    rowData.push({
      id: id,
      type: 'row-footer',
      content: rowFooter,
    });
  }

  const customRowAfter = <RowWrappers rowProps={props} rowType='custom-row-after' />;
  if (customRowAfter && props.tableViewDefinition.insertCustomRow) {
    rowData.push({
      id: id,
      type: 'custom-row-after',
      content: customRowAfter,
    });
  }

  return rowData;
}

export const getDraggableTableRows = <TItem, TAppState>(
  dataGridProps: CfDataGridProps<TItem> | CfDataGridForStoreDataProps<TItem, TAppState>,
  tableViewDefinition: CfDataGridTableViewDefinition<TItem>,
  setNavigableElement: (navigableElement: NavigableElement, rowIndex: number, columnIndex: number) => void,
  isRowDraggable: boolean,
  getFineRowHeight: (item: TItem | undefined, type: CfDataGridRowType) => number
): { itemIds: string[]; rows: CfDraggableRowData[]; estimatedRowSize: number } => {
  const valueGetters = createValueGetters(tableViewDefinition.columns);

  const itemIds: string[] = [];
  const rows: CfDraggableRowData[] = [];
  let estimatedRowSize = 0;

  if ('data' in dataGridProps) {
    const items = getItems(dataGridProps.data);
    const itemCount = items.filter((i) => !i.isLoading).length;
    items.forEach(({ item }, index: number) => {
      const id = item ? dataGridProps.selectId(item) : undefined;
      const newGridRows = GetDraggableRowData(
        {
          item: item,
          previousItem: items[index - 1]?.item,
          valueGetters: valueGetters,
          tableViewDefinition: tableViewDefinition,
          itemIndex: index,
          setNavigableElement: setNavigableElement,
          onItemClicked: dataGridProps.onItemClicked,
          cursorStyle: dataGridProps.cursorStyle,
          hoverStyle: dataGridProps.hoverStyle,
          filterText: getFilterText(dataGridProps),
          itemCount: itemCount,
          recomputeRowHeights: undefined,
          recomputeRowHeight: undefined,
        },
        id,
        getFineRowHeight,
        isRowDraggable
      );
      if (id) itemIds.push(id);
      newGridRows.forEach((r) => {
        rows.push(r);
        estimatedRowSize += r.rowHeight;
      });
      if (id) itemIds.push(id);
    });
  } else {
    // Drag and drop functionality requires pre-determined row heights
    console.error('Error! CfDataGrid with StoreData does not support drag and drop functionality.');
  }

  estimatedRowSize = estimatedRowSize / rows.length;
  return { itemIds, rows, estimatedRowSize };
};

export const getVirtualizedTableRows = <TItem, TAppState>(
  dataGridProps: CfDataGridProps<TItem> | CfDataGridForStoreDataProps<TItem, TAppState>,
  tableViewDefinition: CfDataGridTableViewDefinition<TItem>,
  setNavigableElement: (navigableElement: NavigableElement, rowIndex: number, columnIndex: number) => void,
  recomputeRowHeightsByIndex: (idx: number) => void,
  debouncedRecomputeRowHeights: debounce<() => void>
): { itemIds: string[]; rows: CfRowData[] } => {
  const itemIds: string[] = [];
  const rows: CfRowData[] = [];
  //console.log('get table rows');
  if (!getHasItems(dataGridProps)) {
    rows.push({
      type: 'no-items-row',
      content: (
        <CfDataGridNoResultsFound
          message={dataGridProps.noItemsMessage}
          sx={dataGridProps.noItemsMessageSx}
          displayMode='table'
        />
      ),
    });
  } else {
    if ('data' in dataGridProps) {
      const items = getItems(dataGridProps.data);
      const itemCount = items.filter((i) => !i.isLoading).length;
      items.forEach(({ item }, index: number) => {
        if (item) itemIds.push(dataGridProps.selectId(item));
        const newRows = getRowData({
          item: item,
          previousItem: items[index - 1]?.item,
          valueGetters: createValueGetters(tableViewDefinition.columns),
          tableViewDefinition: tableViewDefinition,
          itemIndex: index,
          setNavigableElement: setNavigableElement,
          onItemClicked: dataGridProps.onItemClicked,
          cursorStyle: dataGridProps.cursorStyle,
          hoverStyle: dataGridProps.hoverStyle,
          filterText: getFilterText(dataGridProps),
          itemCount: itemCount,
          recomputeRowHeights: debouncedRecomputeRowHeights,
          recomputeRowHeight: () => recomputeRowHeightsByIndex(index),
        });
        rows.push(...newRows);
      });
    } else {
      const itemIdsWithLoading = getItemIds(dataGridProps.storeData);
      const itemCount = itemIdsWithLoading.filter((i) => !i.isLoading).length;
      itemIdsWithLoading.forEach(({ itemId }, index: number) => {
        if (itemId) itemIds.push(itemId);
        const newRows = getRowDataById({
          itemId: itemId,
          previousItemId: itemIdsWithLoading[index - 1]?.itemId,
          itemSelector: dataGridProps.storeData.itemSelector,
          useSelector: dataGridProps.storeData.useSelector,
          valueGetters: createValueGetters(tableViewDefinition.columns),
          tableViewDefinition: tableViewDefinition,
          itemIndex: index,
          setNavigableElement: setNavigableElement,
          onItemClicked: dataGridProps.onItemClicked,
          cursorStyle: dataGridProps.cursorStyle,
          hoverStyle: dataGridProps.hoverStyle,
          filterText: getFilterText(dataGridProps),
          itemCount: itemCount,
          recomputeRowHeights: debouncedRecomputeRowHeights,
          recomputeRowHeight: () => recomputeRowHeightsByIndex(index),
        });
        rows.push(...newRows);
      });
    }
  }

  return { itemIds, rows };
};

export const detectTableRowsChanged = (
  itemIds: string[],
  prevItemIds: string[] | undefined
): { index: number; differenceFound: boolean } => {
  let index = 0;
  let differenceFound = false;

  for (index = 0; index < itemIds.length; index++) {
    if (prevItemIds === undefined) break;
    if (prevItemIds[index] !== itemIds[index]) {
      differenceFound = true;
      break;
    }
  }
  return { index, differenceFound };
};

export const getTableRowsPerItem = <TItem,>(tableViewDefinition: CfDataGridTableViewDefinition<TItem>): number => {
  let numRows = 1;
  if (tableViewDefinition.insertCustomRow) numRows += 2;
  if (tableViewDefinition.insertFooterRow) numRows += 1;
  return numRows;
};

export const getTableRowIndex = <TItem,>(
  itemIndex: number,
  tableViewDefinition: CfDataGridTableViewDefinition<TItem>
): number => {
  const numRows = getTableRowsPerItem(tableViewDefinition);
  return itemIndex * numRows;
};

export const recomputeTableRowHeights = (
  listRef: React.MutableRefObject<List | null>,
  cache: CellMeasurerCache
): void => {
  if (listRef.current) {
    cache.clearAll();
    listRef.current?.forceUpdateGrid();

    //console.log('recompute row heights');
  }
};

export const recomputeTableRowHeightsFromIndex = <TItem,>(
  tableViewDefinition: CfDataGridTableViewDefinition<TItem>,
  listRef: React.MutableRefObject<List | null>,
  cache: CellMeasurerCache,
  length: number,
  i: number
): void => {
  if (listRef.current) {
    // Recompute previous item's rows. Custom rows and footers may have changed with the addition of a new item.
    if (i > 0) i--;

    const start = getTableRowIndex(i, tableViewDefinition);
    const end = length * getTableRowsPerItem(tableViewDefinition) - 1;

    for (let i = start; i <= end; i++) cache.clear(i, 0);
    listRef.current.forceUpdateGrid();

    //console.log('recompute row heights from', i);
  }
};

export const recomputeTableRowHeightsByIndex = <TItem,>(
  tableViewDefinition: CfDataGridTableViewDefinition<TItem>,
  listRef: React.MutableRefObject<List | null>,
  cache: CellMeasurerCache,
  i: number
): void => {
  if (listRef.current) {
    const start = getTableRowIndex(i, tableViewDefinition);
    const end = start + getTableRowsPerItem(tableViewDefinition) - 1;

    for (let i = start; i <= end; i++) cache.clear(i, 0);
    listRef.current.forceUpdateGrid();
    //console.log('recompute row heights by', i);
  }
};
